// ---------------------------------------------------------------------------
// parser.ts  ·  Decoder for Echometer TWM .tam / .TAM_EXP files
//
// A .TAM_EXP is a ZIP archive with this layout:
//   Wells/<Name>_{GUID}/<Name>.{HEADER}            -> well identity
//   Wells/<Name>_{GUID}/<Name>.<date>              -> well config (mechanical)
//   Wells/<Name>_{GUID}/Test_Data/YYYY/MM/DD/
//        <Name>.DYN.<date>.<date>                  -> dynamometer test
//        <Name>.ACU.<date>.<date>                  -> acoustic (fluid level) test
//
// Each binary file is a Delphi/C++Builder object stream. We don't decode the
// full grammar; instead we robustly extract UTF-16LE strings and float32 arrays.
// Ported verbatim (behaviour-identical) from TamWeb; types added for gto strict.
// ---------------------------------------------------------------------------
import type {
  ParsedWell, TamDynTest, TamAcuTest, TamConfig, TamHeader,
  RodSection, PipeSection, FloatArrayBlock, ScannedString, BuildupData,
  JsZipLike, TamSensor, Stats, PumpInfo, SurfaceUnit, TamStroke,
} from './types'

function dv(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
}

// Read a Float32Array located at byteOffset within `bytes` (handles misalignment).
function f32at(bytes: Uint8Array, byteOffset: number, n: number): Float32Array {
  const start = bytes.byteOffset + byteOffset
  const slice = bytes.buffer.slice(start, start + n * 4)
  return new Float32Array(slice)
}

function isCleanFloats(arr: Float32Array): boolean {
  let nonZero = 0
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i]
    if (!Number.isFinite(v) || Math.abs(v) > 1e7) return false
    if (Math.abs(v) > 1e-6) nonZero++
  }
  return nonZero / arr.length > 0.25
}

// Scan the whole buffer for `int32 count` + `count float32` blocks.
export function scanFloatArrays(bytes: Uint8Array, minN = 200, maxN = 2_000_000): FloatArrayBlock[] {
  const view = dv(bytes)
  const out: FloatArrayBlock[] = []
  const len = bytes.byteLength
  let p = 0
  while (p < len - 8) {
    const n = view.getInt32(p, true)
    if (n >= minN && n < maxN && p + 4 + n * 4 <= len) {
      const arr = f32at(bytes, p + 4, n)
      if (isCleanFloats(arr)) {
        out.push({ offset: p, data: arr })
        p += 4 + n * 4
        continue
      }
    }
    p++
  }
  return out
}

// Scan for UTF-16LE strings (Delphi marker 0xFF 0xFE 0xFF + len byte).
export function scanStrings(bytes: Uint8Array, maxLen = 80): ScannedString[] {
  const out: ScannedString[] = []
  const len = bytes.byteLength
  let i = 0
  while (i < len - 4) {
    if (bytes[i] === 0xff && bytes[i + 1] === 0xfe && bytes[i + 2] === 0xff) {
      const ln = bytes[i + 3]
      if (ln > 0 && ln < maxLen && i + 4 + ln * 2 <= len) {
        let s = ''
        let ok = true
        for (let k = 0; k < ln; k++) {
          const code = bytes[i + 4 + k * 2] | (bytes[i + 4 + k * 2 + 1] << 8)
          if (code === 0) { ok = false; break }
          s += String.fromCharCode(code)
        }
        if (ok && /^[\x20-\x7E -￿]+$/.test(s)) {
          out.push({ offset: i, value: s })
          i += 4 + ln * 2
          continue
        }
      }
    }
    i++
  }
  return out
}

function stats(arr: ArrayLike<number>): Stats {
  let mn = Infinity, mx = -Infinity, sum = 0
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i]
    if (v < mn) mn = v
    if (v > mx) mx = v
    sum += v
  }
  return { min: mn, max: mx, mean: sum / arr.length, n: arr.length }
}

// Parse "MM-DD-YYYY_HH-MM-SS" out of a test filename.
function dateFromName(name: string): Date | null {
  const m = name.match(/(\d{2})-(\d{2})-(\d{4})_(\d{2})-(\d{2})-(\d{2})/)
  if (!m) return null
  const [, mm, dd, yyyy, h, mi, s] = m
  return new Date(+yyyy, +mm - 1, +dd, +h, +mi, +s)
}

// Channel/field labels we never want to treat as the test description.
const LABEL_BLOCKLIST = /^(Acquired Pressure|Sampled Pressure|Temp|Volts|Load|Accel|Position|DYN|ACU|CDSChannel)$/i

// Pick the most "description-like" string.
function pickDescription(strings: ScannedString[]): string {
  const cands = strings
    .map((s) => s.value)
    .filter((v) => /[A-Za-z].*[ ].*[A-Za-z]/.test(v) && !/Release|GUID|\{|^\d/.test(v) && !LABEL_BLOCKLIST.test(v))
  const preferred = cands.filter((v) => /nivel|carta|prueba|spm|psi|v[aá]lvula/i.test(v))
  const pool = preferred.length ? preferred : cands
  let best = ''
  for (const v of pool) if (v.length > best.length) best = v
  return best
}

// ---------------------------------------------------------------------------
// Signal helpers for DYN reconstruction
// ---------------------------------------------------------------------------

// Cumulative trapezoidal integration.
function integrate(y: ArrayLike<number>, dt: number): Float64Array {
  const n = y.length
  const out = new Float64Array(n)
  let acc = 0
  for (let i = 1; i < n; i++) {
    acc += (y[i] + y[i - 1]) * 0.5 * dt
    out[i] = acc
  }
  return out
}

// Remove the straight line between first and last sample (kills endpoint drift).
function detrendLinear(x: ArrayLike<number>): Float64Array {
  const n = x.length
  const out = new Float64Array(n)
  const a = x[0], b = x[n - 1]
  for (let i = 0; i < n; i++) out[i] = x[i] - (a + (b - a) * (i / (n - 1)))
  return out
}

// Subtract a centered moving average (high-pass) to remove slow drift.
function detrendMovingAvg(x: ArrayLike<number>, win: number): Float64Array {
  const n = x.length
  const out = new Float64Array(n)
  const half = Math.max(1, Math.floor(win / 2))
  const ps = new Float64Array(n + 1)
  for (let i = 0; i < n; i++) ps[i + 1] = ps[i] + x[i]
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - half)
    const b = Math.min(n, i + half + 1)
    out[i] = x[i] - (ps[b] - ps[a]) / (b - a)
  }
  return out
}

// Dominant period (in samples) via autocorrelation within [lo, hi].
function dominantPeriod(x: ArrayLike<number>, lo: number, hi: number): number {
  const n = x.length
  let mean = 0
  for (let i = 0; i < n; i++) mean += x[i]
  mean /= n
  let best = lo, bestVal = -Infinity
  for (let lag = lo; lag <= hi; lag++) {
    let s = 0
    for (let i = 0; i + lag < n; i++) s += (x[i] - mean) * (x[i + lag] - mean)
    if (s > bestVal) { bestVal = s; best = lag }
  }
  return best
}

// Find the byte offset of a UTF-16LE channel name (marker FF FE FF + len).
function findNameOffset(bytes: Uint8Array, name: string, from = 0): number {
  const len = bytes.length
  for (let i = from; i < len - 4; i++) {
    if (bytes[i] === 0xff && bytes[i + 1] === 0xfe && bytes[i + 2] === 0xff && bytes[i + 3] === name.length) {
      let ok = true
      for (let k = 0; k < name.length; k++) {
        const c = bytes[i + 4 + k * 2] | (bytes[i + 4 + k * 2 + 1] << 8)
        if (c !== name.charCodeAt(k)) { ok = false; break }
      }
      if (ok) return i
    }
  }
  return -1
}

// Within [from,to), return the LAST big float array (N>=minN) fully contained.
function lastBigArrayIn(bytes: Uint8Array, from: number, to: number, minN = 800): Float32Array | null {
  const view = dv(bytes)
  let best: Float32Array | null = null
  let p = from
  while (p < to - 8) {
    const n = view.getInt32(p, true)
    if (n >= minN && p + 4 + n * 4 <= to) {
      const a = f32at(bytes, p + 4, n)
      if (isCleanFloats(a)) best = a // keep overwriting -> last wins
    }
    p++
  }
  return best
}

// Local minima spaced at least `dist` apart (keeps the deeper one in a cluster).
function findMinima(x: ArrayLike<number>, dist: number): number[] {
  const mins: number[] = []
  const n = x.length
  for (let i = 1; i < n - 1; i++) {
    if (x[i] <= x[i - 1] && x[i] < x[i + 1]) {
      const last = mins[mins.length - 1]
      if (last != null && i - last < dist) {
        if (x[i] < x[last]) mins[mins.length - 1] = i
      } else {
        mins.push(i)
      }
    }
  }
  return mins
}

// ---------------------------------------------------------------------------
// DYN — dynamometer test
// ---------------------------------------------------------------------------
export function parseDyn(bytes: Uint8Array, fileName: string): TamDynTest {
  const strings = scanStrings(bytes)
  const description = pickDescription(strings)
  const spmMatch = description.match(/(\d+(?:[.,]\d+)?)\s*spm/i)
  const spm: number | null = spmMatch ? parseFloat(spmMatch[1].replace(',', '.')) : null
  const isValve = /v[aá]lvula|valve/i.test(description)
  let sensor: TamSensor = /\bPRT\b/i.test(description) ? 'PRT' : (/\bHT\b/i.test(description) ? 'HT' : null)

  const loadOff = findNameOffset(bytes, 'Load')
  const accelOff = findNameOffset(bytes, 'Accel', loadOff >= 0 ? loadOff + 4 : 0)
  let cbase = -1
  for (let i = 0; i < bytes.length - 11; i++) {
    if (bytes[i] === 0x43 && bytes[i + 1] === 0x42 && bytes[i + 2] === 0x61) { cbase = i; break }
  }
  const loadEnd = accelOff >= 0 ? accelOff : bytes.length
  const accelEnd = cbase >= 0 ? cbase : bytes.length
  let load = loadOff >= 0 ? lastBigArrayIn(bytes, loadOff, loadEnd) : null
  let accel = accelOff >= 0 ? lastBigArrayIn(bytes, accelOff, accelEnd) : null
  if (!load || !accel) {
    const big = scanFloatArrays(bytes, 20).filter((a) => a.data.length >= 800)
    if (!load && big[0]) load = big[0].data
    if (!accel && big[1]) accel = big[1].data
  }
  if (!sensor && load) {
    let m = 0; for (let i = 0; i < load.length; i++) m += load[i]; m /= load.length
    sensor = m < -1 ? 'PRT' : 'HT'
  }

  const strokes: TamStroke[] = []
  let positionUnit = 'rel'
  let dynLoadStats: Stats | null = null
  let loadRel = load

  let loadZero = 0
  if (load && sensor === 'PRT') {
    let m = 0; for (let i = 0; i < load.length; i++) m += load[i]; loadZero = m / load.length
    loadRel = new Float32Array(load.length)
    for (let i = 0; i < load.length; i++) loadRel[i] = load[i] - loadZero
  }

  if (load && accel && !isValve) {
    const N = Math.min(load.length, accel.length)
    let am = 0; for (let i = 0; i < N; i++) am += accel[i]; am /= N
    const a0 = new Float64Array(N); for (let i = 0; i < N; i++) a0[i] = accel[i] - am

    let posR = detrendLinear(integrate(detrendLinear(integrate(a0, 1)), 1))
    const period = dominantPeriod(posR, Math.max(20, Math.floor(N / 60)), Math.floor(N / 3))
    posR = detrendMovingAvg(posR, Math.round(period * 1.5))
    const bounds = findMinima(posR, Math.round(period * 0.6))

    const segLens: number[] = []
    for (let i = 0; i < bounds.length - 1; i++) segLens.push(bounds[i + 1] - bounds[i])
    const sortedLens = [...segLens].sort((a, b) => a - b)
    const medLen = sortedLens[Math.floor(sortedLens.length / 2)] || period

    let gm = 0; for (let i = 0; i < N; i++) gm += load[i]; gm /= N

    positionUnit = '%'
    for (let i = 0; i < bounds.length - 1; i++) {
      const s = bounds[i], e = bounds[i + 1], n = e - s
      if (n < medLen * 0.7) continue
      const seg = detrendLinear(posR.subarray(s, e))
      let mn = Infinity, mx = -Infinity
      for (let k = 0; k < n; k++) { if (seg[k] < mn) mn = seg[k]; if (seg[k] > mx) mx = seg[k] }
      const span = mx - mn || 1
      let sm = 0; for (let k = s; k < e; k++) sm += load[k]; sm /= n
      const base = sensor === 'PRT' ? 0 : gm
      const pos = new Float32Array(n)
      const ld = new Float32Array(n)
      for (let k = 0; k < n; k++) {
        pos[k] = ((seg[k] - mn) / span) * 100
        ld[k] = load[s + k] - sm + base
      }
      strokes.push({ position: pos, load: ld, startIndex: s })
    }
    if (strokes.length) {
      let mn = Infinity, mx = -Infinity
      for (const st of strokes) {
        for (let i = 0; i < st.load.length; i++) {
          const v = st.load[i]
          if (v < mn) mn = v
          if (v > mx) mx = v
        }
      }
      dynLoadStats = { min: mn, max: mx, mean: 0, n: N }
    }
  }

  // Newer TAM exports (v2.x) drop the "X spm" text. Derive spm from stroke timing.
  let spmEff = spm
  let spmDerived = false
  if (spmEff == null && strokes.length >= 2 && load && load.length) {
    const view = dv(bytes)
    let rate = (0xbd01 + 8 <= bytes.byteLength) ? view.getFloat64(0xbd01, true) : NaN
    if (!(rate >= 10 && rate <= 240)) rate = 31.25
    const starts = strokes.map((s) => s.startIndex).sort((a, b) => a - b)
    const diffs: number[] = []; for (let i = 1; i < starts.length; i++) diffs.push(starts[i] - starts[i - 1])
    diffs.sort((a, b) => a - b)
    const medSamp = diffs.length ? diffs[Math.floor(diffs.length / 2)] : (load.length / strokes.length)
    if (medSamp > 0) { spmEff = Math.round((60 * rate / medSamp) * 100) / 100; spmDerived = true }
  }

  return {
    kind: 'DYN',
    fileName,
    date: dateFromName(fileName),
    description,
    spm: spmEff,
    spmDerived,
    isValve,
    sensor,
    relative: sensor === 'PRT',
    strokes,
    strokeCount: strokes.length,
    positionUnit,
    channels: { load, accel, loadRel },
    loadStats: dynLoadStats || (load ? stats(load) : null),
    accelStats: accel ? stats(accel) : null,
    strings: strings.map((s) => s.value),
  }
}

// ---------------------------------------------------------------------------
// ACU — acoustic (fluid level) test
// ---------------------------------------------------------------------------
function parseBuildup(bytes: Uint8Array): BuildupData | null {
  const view = dv(bytes), len = bytes.byteLength
  let best: { o: number; c: number; mn: number; mx: number; rng: number } | null = null
  let o = 0
  while (o < len - 4) {
    const c = view.getInt32(o, true)
    if (c >= 10 && c <= 400 && o + 4 + c * 4 <= len) {
      let ok = true, mn = 1e30, mx = -1e30, asc = 0, desc = 0
      let prev: number | null = null
      for (let k = 0; k < c; k++) {
        const f = view.getFloat32(o + 4 + k * 4, true)
        if (!isFinite(f) || Math.abs(f) > 5000) { ok = false; break }
        if (f < mn) mn = f; if (f > mx) mx = f
        if (prev != null) { if (f > prev) asc++; else if (f < prev) desc++ }
        prev = f
      }
      if (ok) {
        const mono = Math.max(asc, desc) / (c - 1), rng = mx - mn
        if (mono >= 0.9 && rng > 0.5 && rng < 3000 && mn > -50) { if (!best || rng > best.rng) best = { o, c, mn, mx, rng } }
        o += 4 + c * 4; continue
      }
    }
    o++
  }
  if (!best) return null
  const curve: number[] = []
  for (let k = 0; k < best.c; k++) curve.push(Math.round(view.getFloat32(best.o + 4 + k * 4, true) * 1000) / 1000)
  const dp = Math.round((curve[curve.length - 1] - curve[0]) * 1000) / 1000
  const intervalSec = 5
  const durationSec = (curve.length - 1) * intervalSec
  const slopePsiMin = durationSec ? Math.round((dp / (durationSec / 60)) * 1000) / 1000 : null
  return { curve, dp, durationSec, intervalSec, slopePsiMin }
}

export function parseAcu(bytes: Uint8Array, fileName: string): TamAcuTest {
  const arrays = scanFloatArrays(bytes)
  const strings = scanStrings(bytes)
  const description = pickDescription(strings)
  const psiMatch = description.match(/(\d+)\s*psi/i)
  const pressure: number | null = psiMatch ? parseInt(psiMatch[1], 10) : null

  const rawTrace = arrays[0] ? arrays[0].data : null
  const filtered = arrays[1] ? arrays[1].data : null
  const processed = arrays[3] ? arrays[3].data : null
  const depthAxis = arrays[5] ? arrays[5].data : null

  const view = dv(bytes)
  const dbl = (o: number): number | null => (o + 8 <= bytes.byteLength ? view.getFloat64(o, true) : null)
  const plausible = (v: number | null, lo: number, hi: number): number | null =>
    (v != null && isFinite(v) && v >= lo && v <= hi ? Math.round(v * 10) / 10 : null)
  let fluidLevelFt = plausible(dbl(0x08), 20, 25000)
  let acousticVelFtS = plausible(dbl(0x10), 400, 5000)
  const rttRaw = dbl(0xb0)
  const rttTimeSec = (rttRaw != null && isFinite(rttRaw) && rttRaw >= 0.05 && rttRaw <= 12)
    ? Math.round(rttRaw * 1e4) / 1e4 : null

  if (fluidLevelFt == null && acousticVelFtS != null && rttTimeSec != null) {
    fluidLevelFt = Math.round(acousticVelFtS * rttTimeSec / 2 * 10) / 10
  } else if (acousticVelFtS == null && fluidLevelFt != null && rttTimeSec) {
    acousticVelFtS = Math.round((2 * fluidLevelFt / rttTimeSec) * 10) / 10
  }
  if (fluidLevelFt == null) {
    fluidLevelFt = plausible(dbl(0x9c), 50, 20000) || plausible(dbl(0x94), 50, 20000)
  }

  return {
    kind: 'ACU',
    fileName,
    date: dateFromName(fileName),
    description,
    pressure,
    rawTrace,
    filtered,
    processed,
    depthAxis,
    fluidLevelFt,
    acousticVelFtS,
    rttTimeSec,
    buildup: parseBuildup(bytes),
    arrayCount: arrays.length,
    arrayStats: arrays.map((a) => stats(a.data)),
    strings: strings.map((s) => s.value),
  }
}

// ---------------------------------------------------------------------------
// Well config — mechanical diagram source
// ---------------------------------------------------------------------------
const COMPONENT_NAMES = new Set<string>([
  'Pump', 'Tubing ID Change', 'Inner Tubing OD Change', 'Outer Tubing OD Change',
  'Perforation', 'Liner', 'Slotted Liner', 'End Of Tubing', 'Casing Shoe', 'Anchor', 'Packer',
  'Gas Anchor', 'Seating Nipple', 'Tubing Anchor',
])

function parseRodString(bytes: Uint8Array): RodSection[] {
  const view = dv(bytes)
  const len = bytes.byteLength
  const rods: RodSection[] = []
  for (let p = 24; p < len - 8; p++) {
    const d = view.getFloat64(p, true)
    if (!(d >= 0.5 && d <= 1.5)) continue
    if (Math.abs(d * 16 - Math.round(d * 16)) > 1e-6) continue
    const wgt = view.getFloat64(p - 24, true)
    const lenFt = view.getFloat64(p - 8, true)
    const expected = 2.93 * d * d - 0.06 * d + 0.02
    if (Math.abs(wgt - expected) < 0.05 && lenFt >= 30 && lenFt <= 8000) {
      rods.push({
        diameterIn: Math.round(d * 1000) / 1000,
        weightPerFt: Math.round(wgt * 1e4) / 1e4,
        lengthFt: Math.round(lenFt * 10) / 10,
      })
      p += 8
    }
  }
  return rods
}

// Buoyant rod weight in lb (mirrors gto computeWrfLb).
export function computeWrf(
  rods: RodSection[] | null | undefined,
  { api = 11, waterCutPct = 50 }: { api?: number; waterCutPct?: number } = {},
): number | null {
  if (!rods || !rods.length) return null
  let totalWeight = 0, totalVolume = 0
  for (const r of rods) {
    const d = r.diameterIn, l = r.lengthFt
    if (!d || !l) continue
    const wpf = r.weightPerFt ?? (2.93 * d * d - 0.06 * d + 0.02)
    totalWeight += wpf * l
    totalVolume += (Math.PI / 4) * d * d / 144 * l
  }
  if (totalWeight <= 0) return null
  const oilSg = 141.5 / (api + 131.5)
  const wc = waterCutPct / 100
  const sgMix = 1.05 * wc + oilSg * (1 - wc)
  return totalWeight - sgMix * 62.4 * totalVolume
}

// Tubing/casing sections are records [top, bottom, ID, OD, weight].
function parsePipes(bytes: Uint8Array): PipeSection[] {
  const view = dv(bytes)
  const len = bytes.byteLength
  const out: PipeSection[] = []
  for (let p = 40; p < len - 16; p++) {
    const od = view.getFloat64(p, true)
    if (!(od >= 1.5 && od <= 13.5)) continue
    if (Math.abs(od * 8 - Math.round(od * 8)) > 0.01) continue
    const id = view.getFloat64(p - 8, true)
    const wt = view.getFloat64(p + 8, true)
    const bot = view.getFloat64(p - 30, true)
    const top = view.getFloat64(p - 38, true)
    if (!(id >= 0.5 && id < od)) continue
    if (!(wt >= 1 && wt <= 80)) continue
    if (!(top >= 0 && top < bot && bot <= 20000)) continue
    out.push({
      topFt: Math.round(top), bottomFt: Math.round(bot),
      odIn: Math.round(od * 1000) / 1000, weightLbFt: Math.round(wt * 100) / 100,
      idIn: Math.round(id * 1000) / 1000,
    })
    p += 8
  }
  const seen = new Set<string>(); const res: PipeSection[] = []
  for (const r of out) { const k = `${r.topFt}-${r.bottomFt}-${r.odIn}`; if (!seen.has(k)) { seen.add(k); res.push(r) } }
  return res
}

function parsePump(bytes: Uint8Array, intakeDepthFt: number | null): PumpInfo {
  const view = dv(bytes)
  const len = bytes.byteLength
  for (let p = 40; p < len - 0x20; p++) {
    const pd = view.getFloat64(p, true)
    const pl = view.getFloat64(p + 8, true)
    if (pd >= 1 && pd <= 3.5 && Math.abs(pd * 4 - Math.round(pd * 4)) < 1e-6
        && pl >= 4 && pl <= 40 && Math.abs(pl - Math.round(pl)) < 1e-6) {
      const plen = view.getFloat64(p + 0x1c, true)
      return {
        plungerDiaIn: Math.round(pd * 1000) / 1000,
        pumpLenFt: Math.round(pl * 10) / 10,
        plungerLenIn: (plen >= 1 && plen <= 200) ? Math.round(plen * 10) / 10 : null,
        intakeDepthFt: intakeDepthFt ?? null,
      }
    }
  }
  return { intakeDepthFt: intakeDepthFt ?? null }
}

function parseSurfaceUnit(bytes: Uint8Array, allStrings: string[], unit: string | null): SurfaceUnit {
  const view = dv(bytes)
  const len = bytes.byteLength
  let manufacturer: string | null = null
  if (unit) { const i = allStrings.indexOf(unit); if (i > 0) manufacturer = allStrings[i - 1] }
  let strokeLengthIn: number | null = null
  const m = unit ? unit.match(/-(\d+)$/) : null
  const maxStroke = m ? +m[1] : null
  if (maxStroke) {
    for (let p = 40; p < len - 16; p++) {
      if (Math.abs(view.getFloat64(p, true) - maxStroke) < 1e-6) {
        const nx = view.getFloat64(p + 8, true)
        if (nx >= maxStroke * 0.35 && nx <= maxStroke * 1.05) { strokeLengthIn = Math.round(nx * 100) / 100; break }
      }
    }
    if (strokeLengthIn == null) strokeLengthIn = maxStroke
  }
  return { manufacturer, designation: unit || null, strokeLengthIn, maxStrokeIn: maxStroke }
}

function parsePolishedRod(bytes: Uint8Array): number | null {
  const view = dv(bytes)
  for (let p = 0x300; p < Math.min(bytes.byteLength - 8, 0x360); p++) {
    const v = view.getFloat64(p, true)
    if (v >= 1.0 && v <= 1.75 && Math.abs(v * 8 - Math.round(v * 8)) < 1e-6) return Math.round(v * 1000) / 1000
  }
  return null
}

export function parseConfig(bytes: Uint8Array, fileName: string): TamConfig {
  const view = dv(bytes)
  const len = bytes.byteLength
  const components: { type: string; depth: number | null }[] = []
  const allStrings: string[] = []
  let i = 0
  while (i < len - 4) {
    if (bytes[i] === 0xff && bytes[i + 1] === 0xfe && bytes[i + 2] === 0xff) {
      const ln = bytes[i + 3]
      if (ln > 0 && ln < 60 && i + 4 + ln * 2 <= len) {
        let s = ''
        let ok = true
        for (let k = 0; k < ln; k++) {
          const code = bytes[i + 4 + k * 2] | (bytes[i + 4 + k * 2 + 1] << 8)
          if (code === 0) { ok = false; break }
          s += String.fromCharCode(code)
        }
        if (ok) {
          allStrings.push(s)
          const strEnd = i + 4 + ln * 2
          if (COMPONENT_NAMES.has(s)) {
            let depth: number | null = null
            for (let p = strEnd; p < Math.min(len - 8, strEnd + 44); p++) {
              const v = view.getFloat64(p, true)
              if (v >= 1 && v < 30000) { depth = Math.round(v * 10) / 10; break }
            }
            components.push({ type: s, depth })
          }
          i = strEnd
          continue
        }
      }
    }
    i++
  }

  const pumpType = allStrings.find((s) => /Pump|Rod/.test(s) && s.length > 5) || null
  const rodDesign = allStrings.find((s) => /^\d+-\d+-\d+$/.test(s)) || null
  const grade = allStrings.find((s) => /^[A-Z]-\d/.test(s)) || null
  const operator = allStrings.find((s) => /\//.test(s)) || null
  const unit = allStrings.find((s) => /^[A-Z]-?\d+D?-\d+-\d+$/.test(s)) || null

  const rods = parseRodString(bytes)
  const rodTotalFt = rods.reduce((s, r) => s + r.lengthFt, 0) || null
  const wrfLb = computeWrf(rods)

  const pipes = parsePipes(bytes)
  const tubing = pipes.filter((p) => p.odIn <= 4.5 && p.weightLbFt <= 14)
  const casing = pipes.filter((p) => !(p.odIn <= 4.5 && p.weightLbFt <= 14))
  const pumpComp = components.find((c) => c.type === 'Pump')
  const pump = parsePump(bytes, pumpComp ? pumpComp.depth : null)
  const surfaceUnit = parseSurfaceUnit(bytes, allStrings, unit)
  const polishedRodIn = parsePolishedRod(bytes)
  const eot = components.find((c) => c.type === 'End Of Tubing')
  const shoe = components.find((c) => c.type === 'Casing Shoe')
  const wellDepthFt = Math.max(
    ...components.map((c) => c.depth || 0),
    ...casing.map((c) => c.bottomFt || 0), 0,
  ) || null

  return {
    kind: 'CONFIG',
    fileName,
    components: components.filter((c) => c.depth != null || c.type === 'Pump'),
    pumpType, rodDesign, grade, operator, unit,
    rods, rodTotalFt, wrfLb,
    tubing, casing, pump, surfaceUnit, polishedRodIn,
    wellDepthFt, eotDepthFt: eot ? eot.depth : null, shoeDepthFt: shoe ? shoe.depth : null,
    strings: allStrings,
  }
}

// ---------------------------------------------------------------------------
// HEADER — well identity
// ---------------------------------------------------------------------------
export function parseHeader(bytes: Uint8Array, fileName: string): TamHeader {
  const strings = scanStrings(bytes).map((s) => s.value)
  const name = strings.find((s) => !/^\{/.test(s)) || strings[0] || ''
  const guid = strings.find((s) => /^\{[0-9A-F-]+\}$/i.test(s)) || null
  const rest = strings.filter((s) => s !== name && s !== guid)
  return { kind: 'HEADER', fileName, name, guid, field: rest[0] || null, company: rest[1] || null, strings }
}

// ---------------------------------------------------------------------------
// Top level — take a JSZip instance and build the well/test tree
// ---------------------------------------------------------------------------
export async function parseTam(zip: JsZipLike): Promise<ParsedWell[]> {
  const wells: Record<string, ParsedWell> = {}

  const entries = Object.values(zip.files).filter((f) => !f.dir)

  for (const entry of entries) {
    const path = entry.name.replace(/\\/g, '/')
    const parts = path.split('/')
    const wIdx = parts.indexOf('Wells')
    if (wIdx < 0 || parts.length < wIdx + 2) continue
    const wellFolder = parts[wIdx + 1]
    const wellName = wellFolder.replace(/_\{[0-9A-F-]+\}$/i, '')
    if (!wells[wellFolder]) {
      wells[wellFolder] = { folder: wellFolder, name: wellName, header: null, config: null, tests: [] }
    }
    const w = wells[wellFolder]
    const base = parts[parts.length - 1]
    const bytes = new Uint8Array(await entry.async('arraybuffer'))

    try {
      if (base.includes('{HEADER}')) {
        w.header = parseHeader(bytes, base)
      } else if (base.includes('.DYN.')) {
        w.tests.push(parseDyn(bytes, base))
      } else if (base.includes('.ACU.')) {
        w.tests.push(parseAcu(bytes, base))
      } else if (parts.includes('Test_Data')) {
        // unknown test type — ignore
      } else if (/\.\d{2}_\d{2}_\d{4}/.test(base) || /\.\d/.test(base)) {
        const cfg = parseConfig(bytes, base)
        if (!w.config || cfg.components.length >= w.config.components.length) w.config = cfg
      }
    } catch (e) {
      console.warn('Failed to parse', base, e)
    }
  }

  const list = Object.values(wells).sort((a, b) => a.name.localeCompare(b.name))
  for (const w of list) {
    w.tests.sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0))
    if (w.header) w.name = w.header.name || w.name
    const wrf = w.config?.wrfLb ?? null
    const su = w.config?.surfaceUnit
    let strokeLen = su?.strokeLengthIn ?? null
    if (strokeLen != null) {
      const max = su?.maxStrokeIn
      if (strokeLen < 15 || strokeLen > 250 || (max && strokeLen > max * 1.2)) strokeLen = null
    }
    const acu = w.tests.filter((x): x is TamAcuTest => x.kind === 'ACU' && x.fluidLevelFt != null).pop()
    const fluidLevelFt = acu ? acu.fluidLevelFt : null
    const casingPressurePsi = acu ? acu.pressure : null
    for (const t of w.tests) {
      if (t.kind === 'DYN') {
        t.wrfLb = wrf
        t.rods = w.config?.rods ?? null
        t.strokeLengthIn = strokeLen
        t.wellName = w.name
        t.fluidLevelFt = fluidLevelFt
        t.pumpDepthFt = w.config?.pump?.intakeDepthFt ?? null
        t.casingPressurePsi = casingPressurePsi
      }
    }
  }
  return list
}
